import { FormHelperText, MenuItem, Select, TextField } from "@mui/material"
import { useEffect, useState } from "react"
import * as yup from "yup"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { KeyboardArrowDown } from "@mui/icons-material"
import FWModal from "../shared/Modal"
import axios from "../../axios"
import toastService from "../../services/toastService"
import { useSelector } from "react-redux"
import { getUser } from "../../store/auth/auth.selectors"
import RunPayrollDropzone from "../run-payroll/RunPayrollDropzone"
import { ReactComponent as DeleteFilled } from "../../assets/images/run-payroll/delete-filled.svg"
import collectClientMetadata from "../../helpers/collectClientMetadata"
import { getFileTypeIcon, isImageFile } from "../../helpers/fileTypeIcon"
import { transformBytesInToKilobytes } from "../../helpers/helpers"
import "./scss/report-issue-modal.scss"

const styles = {
  error: {
    "& div input": {
      borderColor: "#F04438 !important",
      borderLeft: "1px solid #F04438 !important"
    }
  },
  errorSelect: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#F04438 !important"
    }
  }
}

const CATEGORIES = ["Bug", "Feature Request", "Question", "Other"]
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024
const MAX_ATTACHMENT_LABEL = "25MB"

const schema = yup.object().shape({
  subject: yup.string().required("Subject is required"),
  category: yup.string().required("Category is required"),
  description: yup.string().required("Description is required")
})

const ReportIssueModal = ({ open, handleClose }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [files, setFiles] = useState([])
  const [filePreviews, setFilePreviews] = useState([])
  const userData = useSelector(getUser)

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, touchedFields }
  } = useForm({
    mode: "all",
    defaultValues: {
      subject: "",
      category: "",
      description: ""
    },
    resolver: yupResolver(schema)
  })

  useEffect(() => {
    const urls = files.map((f) =>
      isImageFile(f.name, f.type) ? URL.createObjectURL(f) : null
    )
    setFilePreviews(urls)
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u))
  }, [files])

  const handleCurClose = () => {
    handleClose()
    reset()
    setFiles([])
  }

  const handleUploadAttachment = (arg) => {
    const fileList =
      arg?.target?.files ?? arg?.currentTarget?.files ?? arg?.dataTransfer?.files ?? arg

    const incoming = Array.from(fileList ?? [])
    if (!incoming.length) return

    const accepted = []
    let rejectedSize = false

    for (const candidate of incoming) {
      if (candidate.size > MAX_ATTACHMENT_BYTES) {
        rejectedSize = true
        continue
      }
      accepted.push(candidate)
    }

    if (rejectedSize) toastService.error(`Each attachment must be ${MAX_ATTACHMENT_LABEL} or smaller`)

    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted])
    }

    if (arg?.target) {
      try {
        arg.target.value = null
      } catch {
        //
      }
    }
  }

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const onSubmit = (values) => {
    setIsLoading(true)

    const formData = new FormData()
    formData.append("subject", values.subject)
    formData.append("category", values.category)
    formData.append("description", values.description)
    formData.append("user_name", `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim())
    formData.append("user_email", userData?.email || "")

    files.forEach((f) => {
      formData.append("attachments[]", f)
    })

    const metadata = collectClientMetadata()
    Object.entries(metadata).forEach(([key, value]) => {
      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false")
      } else {
        formData.append(key, value ?? "")
      }
    })

    axios
      .post("/issues", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      })
      .then(() => {
        toastService.success(
          "Successfully reported. We may contact you via email for more details."
        )
        handleCurClose()
      })
      .catch((err) => {
        toastService.error(err.response?.data?.message || "Failed to report issue")
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <FWModal
      open={open}
      handleClose={handleCurClose}
      handleConfirm={handleSubmit(onSubmit)}
      isLoading={isLoading}
      confirmButtonText="Submit"
      cancelButtonText="Cancel"
    >
      <div
        style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#333645",
          marginBottom: "20px",
          textAlign: "center"
        }}
      >
        Report an Issue
      </div>

      <form
        className="onboard-form company-add-modal report-issue-modal"
        style={{ textAlign: "left" }}
      >
        <div className="form-item">
          <label className={errors.subject?.message && "error"}>Subject *</label>
          <div className="input-box admin-panel">
            <TextField
              sx={errors.subject?.message && styles.error}
              fullWidth
              type="text"
              placeholder="Brief description of the issue"
              {...register("subject")}
              error={touchedFields.subject && !!errors.subject?.message}
              helperText={errors.subject?.message}
            />
          </div>
        </div>

        <div className="form-item">
          <label className={errors.category?.message && "error"}>Category *</label>
          <div className="input-box admin-panel">
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  sx={errors.category?.message ? styles.errorSelect : {}}
                  className="employees-select admin-panel"
                  fullWidth
                  displayEmpty
                  IconComponent={KeyboardArrowDown}
                >
                  <MenuItem value="" disabled>
                    Select a category
                  </MenuItem>
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {!!errors.category?.message && (
              <FormHelperText error>{errors.category?.message}</FormHelperText>
            )}
          </div>
        </div>

        <div className="notes-into" style={{ textAlign: "left", marginBottom: 16 }}>
          <label className={errors.description?.message && "error"}>Description *</label>
          <TextField
            fullWidth
            multiline
            rows={4}
            type="text"
            placeholder="Please describe the issue in detail"
            {...register("description")}
            error={touchedFields.description && !!errors.description?.message}
            helperText={errors.description?.message}
          />
        </div>

        <div className="form-item">
          <label>Attachments</label>
          <p className="attachment-hint">
            Screenshots, videos, or files help us reproduce the issue faster.
          </p>
          <RunPayrollDropzone
            uploadText={`Click or drag any file (${MAX_ATTACHMENT_LABEL} max) to this area to upload`}
            handleUploadFile={handleUploadAttachment}
            fileFormats="*/*"
          />
          {files.map((f, index) => (
            <div className="attachment-file-row" key={`${f.name}-${f.lastModified}-${index}`}>
              {filePreviews[index] ? (
                <img
                  className="attachment-thumb"
                  src={filePreviews[index]}
                  alt="Attachment preview"
                />
              ) : (
                <div className="attachment-thumb attachment-thumb--icon" aria-hidden="true">
                  {getFileTypeIcon(f.name)}
                </div>
              )}
              <div className="text-container">
                <p className="title">{f.name}</p>
                <p className="description">{transformBytesInToKilobytes(f.size)}KB</p>
              </div>
              <button
                type="button"
                className="attachment-remove-btn"
                onClick={() => handleRemoveFile(index)}
                aria-label="Remove attachment"
              >
                <DeleteFilled />
              </button>
            </div>
          ))}
        </div>
      </form>
    </FWModal>
  )
}

export default ReportIssueModal
